import React, { useMemo } from 'react';
import { C, S } from '../utils/styles';
import { fmt } from '../utils/formatters';

/**
 * Workforce-level Jurisdiction Impacts Panel (Phase 17).
 *
 * Surfaces:
 *   - Total local-income-tax savings unlocked by jurisdiction-aware modeling
 *   - Per-jurisdiction breakdown (employee count + annual local-tax savings)
 *   - Min-wage exclusion analysis (count of employees flipped non-viable
 *     specifically because of a state/city minimum-wage floor higher than
 *     the federal $7.25)
 *
 * Accepts the `analyzed` array from `optimizeEmployeeWithEligibility`.
 * Each row carries `optimal.calc.localTaxJurisdiction` and
 * `optimal.calc.localTaxAnnualSavings`, plus `applicableMinWage` /
 * `minimumWageInfo`.
 */
export default function JurisdictionImpactsPanel({ analyzed }) {
  const rollup = useMemo(() => buildRollup(analyzed || []), [analyzed]);

  if (!analyzed || analyzed.length === 0) return null;

  const showLocalTax = rollup.totalLocalTaxSavings > 0 || rollup.byJurisdiction.length > 0;
  const showMinWage = rollup.minWageImpacts.length > 0;
  if (!showLocalTax && !showMinWage) return null;

  return (
    <div data-testid="jurisdiction-impacts-panel" style={{ ...S.card, marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <div>
          <div style={S.heading}>Jurisdiction Impacts</div>
          <div style={{ fontSize: 12, color: C.textMid, marginTop: 4 }}>
            Additional accuracy unlocked by jurisdiction-aware minimum wage and local income tax.
          </div>
        </div>
        {showLocalTax && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Total local-tax savings unlocked
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.green, fontFamily: "'JetBrains Mono', monospace" }}>
              {fmt(rollup.totalLocalTaxSavings)}/yr
            </div>
            <div style={{ fontSize: 11, color: C.textMid }}>
              {rollup.localTaxEmployeeCount} employee{rollup.localTaxEmployeeCount === 1 ? '' : 's'} across {rollup.byJurisdiction.length} jurisdiction{rollup.byJurisdiction.length === 1 ? '' : 's'}
            </div>
          </div>
        )}
      </div>

      {showLocalTax && (
        <div style={{ overflowX: 'auto', borderTop: `1px solid ${C.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 540 }}>
            <thead>
              <tr>
                <th style={S.th}>Jurisdiction</th>
                <th style={S.th}>Employees</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Local Tax Savings</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Avg / Employee</th>
                <th style={S.th}>Resolution</th>
              </tr>
            </thead>
            <tbody>
              {rollup.byJurisdiction.map((row) => (
                <tr key={row.key} data-testid={`jurisdiction-row-${row.key}`}>
                  <td style={{ ...S.td, fontWeight: 600 }}>{row.key}</td>
                  <td style={S.td}>{row.count}</td>
                  <td style={{ ...S.td, textAlign: 'right', color: C.green, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>
                    {fmt(row.totalSavings)}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace" }}>
                    {fmt(row.totalSavings / row.count)}
                  </td>
                  <td style={S.td}>
                    <ResolutionBadges sources={row.sources} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showMinWage && (
        <div style={{ marginTop: 16, padding: 12, background: C.orangeLight, borderRadius: 8, border: `1px solid #fde68a` }}>
          <div style={{ ...S.heading, color: C.orange, marginBottom: 8 }}>Minimum-Wage Exclusions</div>
          <div style={{ fontSize: 12, color: C.textMid, marginBottom: 10 }}>
            Employees who fail the viability check because their state or city minimum wage exceeds the federal floor. These employees are correctly excluded — using the federal floor would have wrongly marked them eligible.
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {rollup.minWageImpacts.map((row) => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: C.card, borderRadius: 6, fontSize: 13 }}>
                <span><strong>{row.label}</strong> ({fmt(row.applicable)}/hr {row.basis})</span>
                <span style={{ fontWeight: 700, color: C.orange, fontFamily: "'JetBrains Mono', monospace" }}>{row.count} excluded</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ResolutionBadges({ sources }) {
  if (!sources || sources.length === 0) return <span style={{ color: C.textLight }}>—</span>;
  const labels = {
    explicit: { label: 'city column', bg: C.greenLight, color: C.green },
    zip: { label: 'ZIP', bg: C.accentBg, color: C.accent },
    employer_hq: { label: 'employer HQ', bg: C.orangeLight, color: C.orange },
    state_only: { label: 'state', bg: C.bg, color: C.textMid },
  };
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {sources.map((src) => {
        const v = labels[src] || { label: src, bg: C.bg, color: C.textMid };
        return (
          <span key={src} style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, background: v.bg, color: v.color }}>{v.label}</span>
        );
      })}
    </div>
  );
}

function buildRollup(analyzed) {
  const byKey = new Map();
  let totalLocalTaxSavings = 0;
  let localTaxEmployeeCount = 0;

  // Min-wage exclusion analysis
  const FEDERAL_FLOOR = 7.25;
  const minWageBuckets = new Map();

  for (const row of analyzed) {
    const opt = row?.optimal;
    const calc = opt?.calc;
    const savings = Number(calc?.localTaxAnnualSavings || 0);
    const jur = calc?.localTaxJurisdiction;
    if (jur?.key && savings > 0) {
      totalLocalTaxSavings += savings;
      localTaxEmployeeCount += 1;
      const existing = byKey.get(jur.key) || { key: jur.key, count: 0, totalSavings: 0, sources: new Set() };
      existing.count += 1;
      existing.totalSavings += savings;
      if (jur.source) existing.sources.add(jur.source);
      byKey.set(jur.key, existing);
    }

    // Min-wage exclusion: was excluded specifically because of a non-federal floor?
    const elig = row?.eligibility;
    const minInfo = row?.minimumWageInfo || row?.optimal?.minimumWageInfo;
    if (elig && elig.eligible === false && elig.status === 'salary' && minInfo) {
      const applicable = Number(minInfo.applicable || 0);
      if (applicable > FEDERAL_FLOOR + 0.001) {
        const label = minInfo.basis === 'city' && minInfo.jurisdictionKey
          ? minInfo.jurisdictionKey
          : minInfo.basis === 'state'
          ? `${row.state || 'state'} (state floor)`
          : 'state floor';
        const k = `${label}|${applicable}`;
        const cur = minWageBuckets.get(k) || { label, applicable, basis: minInfo.basis, count: 0 };
        cur.count += 1;
        minWageBuckets.set(k, cur);
      }
    }
  }

  const byJurisdiction = [...byKey.values()]
    .map((r) => ({ ...r, sources: [...r.sources] }))
    .sort((a, b) => b.totalSavings - a.totalSavings);

  const minWageImpacts = [...minWageBuckets.values()].sort((a, b) => b.count - a.count);

  return { totalLocalTaxSavings, localTaxEmployeeCount, byJurisdiction, minWageImpacts };
}
